import {
    getNodeId,
    isResponseCode5xx,
    logOperationalMetric,
    chainWalk,
    convertTableJsonToAsset,
    setAliasToRequest,
    checkInactiveOrDeprecatedAsset
} from "../../../util/Util";
import {ACTIVE, VAPR_ASSET_TYPE, DEFAULT_VENDOR_ATTRIBUTES, LOADING_STRING,
    ALERT_UPDATE_ASSET_MESSAGE, ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET,
    ALERT_ASSET_STATUS_MESSAGE} from "../../../util/Constants";
import {getAssets, addAsset, updateAsset, setAssetStatus, getAssetByAssetLabel} from "../../../resources/AssetResource";

const metricProcessName = "VAPR_DEVICE_DETAILS";

export function fetchVaprDeviceDetails(dispatcher) {
    const request = {
        nodeId: getNodeId(),
        assetType: VAPR_ASSET_TYPE
    }
    return getVaprDeviceDetails(dispatcher, request)
}

async function getVaprDeviceDetails(dispatcher, request) {
    const model = {
        vaprDeviceInformation: {},
        macAddressArray: []
    }
    let pageToken = null;
    do {
        const startTime = Date.now();
        const getAssetsResult = await getAssets(request.nodeId, request.assetType, pageToken);
        const isSuccess = getAssetsResult.status === 200;
        const isFault = isResponseCode5xx(getAssetsResult.status);
        logOperationalMetric(startTime, metricProcessName, "getVaprDeviceDetails", isSuccess, isFault);

        if (isSuccess) {
            addVaprDeviceDetailsToModel(getAssetsResult, model);
            pageToken = getAssetsResult.data.pageToken;
        } else {
            dispatcher({type: "RECEIVE_DATA", data: model});
            return;
        }
    } while (pageToken);
    dispatcher({type: "RECEIVE_DATA", data: model});
}

const addVaprDeviceDetailsToModel = (result, model) => {
    if ("data" in result && "assetDetailsList" in result.data) {
        result.data.assetDetailsList.forEach((deviceDetail) => {
            if (deviceDetail.assetStatus === ACTIVE) {
                const mac = deviceDetail.assetAttributes.mac;
                const deviceConfig = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.deviceConfig), {});
                const detectionFov = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.detectionFov), {});
                const projectionFov = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.projectionFov), {});
                const monitoring = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.monitoring), {});
                model.macAddressArray.push(mac);
                model.vaprDeviceInformation[mac] = {
                    name: deviceDetail.assetAttributes.name,
                    mac: mac,
                    ip: deviceDetail.assetAttributes.ip,
                    port: deviceDetail.assetAttributes.port,
                    deviceId: deviceConfig.id || "",
                    deviceType: chainWalk(() => deviceDetail.assetAttributes.deviceType, ""),
                    detectionFovLength: detectionFov.length || "",
                    detectionFovBreadth: detectionFov.breadth || "",
                    detectionFovUnit: detectionFov.unit || "mm",
                    projectionFovLength: projectionFov.length || "",
                    projectionFovBreadth: projectionFov.breadth || "",
                    projectionFovUnit: projectionFov.unit || "mm",
                    monitoringStartTime: monitoring.startTime || "0",
                    monitoringEndTime: monitoring.endTime || "0",
                    vendorAttributes: deviceDetail.vendorAttributes || DEFAULT_VENDOR_ATTRIBUTES,
                    connectionStatus: LOADING_STRING,
                }
            }
        });
    }
    return model;
}

export function setVaprDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    stateValues.mac = stateValues.mac.toLowerCase();
    const request = convertTableJsonToAsset(stateValues, VAPR_ASSET_TYPE, ACTIVE);
    return addVaprDevice(dispatcher, request, showCustomAlert);
}

async function addVaprDevice(dispatcher, request, showCustomAlert) {
    const startTime = Date.now();
    await setAliasToRequest(request);
    const result = await addAsset(request);
    if (result.status === 200) {
        logOperationalMetric(startTime, metricProcessName, "addVaprDevice", true, false);
    } else {
        const isFault = isResponseCode5xx(result.status);
        const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
        if (!checkInactiveOrDeprecatedAsset(chainWalk(() => getAssetResult.data.asset.assetDetails.assetStatus, null))) {
            showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isFault);
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        const responseSetAssetStatus = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
        if (responseSetAssetStatus.status !== 200) {
            showCustomAlert(ALERT_ASSET_STATUS_MESSAGE);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isResponseCode5xx(responseSetAssetStatus.status));
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        const responseUpdateAsset = await updateAsset(request, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));
        if (responseUpdateAsset.status !== 200) {
            showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isResponseCode5xx(responseUpdateAsset.status));
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        logOperationalMetric(startTime, metricProcessName, "addVaprDevice", true, false);
    }
    fetchVaprDeviceDetails(dispatcher);
}

export function updateVaprDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    stateValues.mac = stateValues.mac.toLowerCase();
    const request = convertTableJsonToAsset(stateValues, VAPR_ASSET_TYPE, ACTIVE);
    return updateVaprDevice(dispatcher, request, showCustomAlert);
}

async function updateVaprDevice(dispatcher, request, showCustomAlert) {
    const startTime = Date.now();
    await setAliasToRequest(request);
    const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
    const creationTime = chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null);
    if (!creationTime) {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
        logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", false, true);
        fetchVaprDeviceDetails(dispatcher);
        return;
    }
    const setAssetStatusResponse = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
    if (setAssetStatusResponse.status !== 200) {
        showCustomAlert(ALERT_ASSET_STATUS_MESSAGE);
        logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", false, isResponseCode5xx(setAssetStatusResponse.status));
        fetchVaprDeviceDetails(dispatcher);
        return;
    }
    const updateAssetResponse = await updateAsset(request, creationTime);
    const isSuccess = updateAssetResponse.status === 200;
    const isFault = isResponseCode5xx(updateAssetResponse.status);
    if (!isSuccess) {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
    }
    logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", isSuccess, isFault);
    fetchVaprDeviceDetails(dispatcher);
}



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/network/VaprDeviceNetworkAPI.js